import React from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getCustomFeeError,
  isCustomFeeSuspect,
  shouldShowCustomFee,
} from '@/utils/importCustomFee';

interface CustomerFeeCellProps {
  fee?: number | null;
  productsValue?: number | null;
  shipmentFeeType: string;
  errors?: Record<string, unknown> | null;
}

/**
 * BOXY-414 — the bulk-import preview's "customer shipping fee" cell. Warns in
 * red when the fee looks like a product price, so the merchant fixes the row
 * before 362,987 IQD of "shipping" reaches a customer.
 */
export const CustomerFeeCell: React.FC<CustomerFeeCellProps> = ({
  fee,
  productsValue,
  shipmentFeeType,
  errors,
}) => {
  const { t } = useTranslation();
  const amount = Number(fee) || 0;

  if (!shouldShowCustomFee(amount, shipmentFeeType, errors)) {
    return <span className='text-sm text-[#737b8c]'>—</span>;
  }

  const suspect = isCustomFeeSuspect(amount, Number(productsValue) || 0, errors);
  const label = `${amount.toLocaleString()} ${String(t('iqd'))}`;

  return suspect ? (
    <div className='sd-scope sd-font flex flex-col items-start gap-1'>
      <span className='text-sm font-semibold text-[#dc2828] whitespace-nowrap'>{label}</span>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type='button'
              className='inline-flex items-center gap-1 rounded-full border border-solid border-[#dc2828]/30 bg-[#dc2828]/10 px-2 py-0.5 text-[11px] font-semibold text-[#dc2828] whitespace-nowrap cursor-help'
            >
              <TriangleAlert className='h-3 w-3 shrink-0' strokeWidth={2} />
              {String(t('custom_fee_looks_like_product_price'))}
            </button>
          </TooltipTrigger>
          <TooltipContent className='max-w-[260px] text-xs'>
            {getCustomFeeError(errors) ?? String(t('customer_shipping_fee_tooltip'))}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  ) : (
    <span className='text-sm text-sd-foreground whitespace-nowrap'>{label}</span>
  );
};

export default CustomerFeeCell;
